import { useState, type ReactNode } from "react";
import { getStrings, homeShellString } from "../i18n/strings";
import { trackEvent } from "../utils/telemetry";

type Strings = ReturnType<typeof getStrings>;

const CLI_COMMAND = "npx netriskscan-cli check 8.8.8.8";
const SDK_INSTALL_COMMAND = "npm install @netriskscan/sdk";
const PYTHON_SDK_INSTALL_COMMAND = "pip install netriskscan";
const DOTNET_SDK_INSTALL_COMMAND = "dotnet add package NetRiskScan";
const CLI_GITHUB_URL = "https://github.com/TeamQQ/netriskscan-cli";
const SDK_GITHUB_URL = "https://github.com/TeamQQ/netriskscan-sdk-js";
const PYTHON_SDK_PYPI_URL = "https://pypi.org/project/netriskscan/";
const DOTNET_SDK_NUGET_URL = "https://www.nuget.org/packages/NetRiskScan";

const SDK_CODE_SAMPLE = `import { NetRiskScanClient } from "@netriskscan/sdk";

const client = new NetRiskScanClient({});
const result = await client.checkIp("8.8.8.8");`;

const PYTHON_SDK_CODE_SAMPLE = `from netriskscan import NetRiskScan

client = NetRiskScan()
result = client.ip_risk("8.8.8.8")`;

const DOTNET_SDK_CODE_SAMPLE = `using NetRiskScan;

using var client = new NetRiskScanClient();
var result = await client.GetIpRiskAsync("8.8.8.8");`;

// Secondary conversion path: turns a visitor reading the IP result into a developer who discovers
// the CLI / SDK and, from there, the Developer API. Renders unconditionally (result or not) — spec
// requires the entry points to be discoverable whether or not a check has run yet. Placed between
// the location panel and "More checks" so it never competes with the primary IP-check flow above it.
export function HomeDeveloperTools({ strings, apiPath }: { strings: Strings; apiPath: string }) {
  const t = <K extends Parameters<typeof homeShellString>[1]>(key: K) => homeShellString(strings, key);

  return (
    <section className="home-developer" aria-labelledby="home-developer-title">
      <header className="home-developer__header">
        <p className="home-developer__eyebrow">{t("homeDeveloperEyebrow")}</p>
        <h2 id="home-developer-title" className="home-developer__title">{t("homeDeveloperTitle")}</h2>
        <p className="home-developer__subtitle">{t("homeDeveloperSubtitle")}</p>
      </header>

      <div className="home-developer__grid">
        <DevCard
          icon={<TerminalIcon />}
          title={t("homeCliTitle")}
          description={t("homeCliDescription")}
          command={CLI_COMMAND}
          note={t("homeCliTrial")}
          resourceUrl={CLI_GITHUB_URL}
          resourceLabel={t("homeCliGithub")}
          copyLabel={t("homeCliCopyCommand")}
          copiedLabel={t("homeCommandCopied")}
        />
        <DevCard
          icon={<BracketsIcon />}
          title={t("homeSdkTitle")}
          description={t("homeSdkDescription")}
          command={SDK_INSTALL_COMMAND}
          resourceUrl={SDK_GITHUB_URL}
          resourceLabel={t("homeSdkGithub")}
          copyLabel={t("homeSdkCopyCommand")}
          copiedLabel={t("homeCommandCopied")}
        >
          <pre className="home-dev-card__sample"><code>{SDK_CODE_SAMPLE}</code></pre>
        </DevCard>
        <DevCard
          icon={<PackageIcon />}
          title={t("homePythonSdkTitle")}
          description={t("homePythonSdkDescription")}
          command={PYTHON_SDK_INSTALL_COMMAND}
          resourceUrl={PYTHON_SDK_PYPI_URL}
          resourceLabel={t("homePythonSdkPypi")}
          copyLabel={t("homePythonSdkCopyCommand")}
          copiedLabel={t("homeCommandCopied")}
        >
          <pre className="home-dev-card__sample"><code>{PYTHON_SDK_CODE_SAMPLE}</code></pre>
        </DevCard>
        <DevCard
          icon={<CubeIcon />}
          title={t("homeDotNetSdkTitle")}
          description={t("homeDotNetSdkDescription")}
          command={DOTNET_SDK_INSTALL_COMMAND}
          resourceUrl={DOTNET_SDK_NUGET_URL}
          resourceLabel={t("homeDotNetSdkNuget")}
          copyLabel={t("homeDotNetSdkCopyCommand")}
          copiedLabel={t("homeCommandCopied")}
        >
          <pre className="home-dev-card__sample"><code>{DOTNET_SDK_CODE_SAMPLE}</code></pre>
        </DevCard>
      </div>

      {/* The reference design closes the page with a full-width "need more?" banner and one button.
          Same two strings as the old inline text link — no new copy, just promoted to the CTA
          treatment the design gives it. */}
      <div className="home-developer__cta">
        <p className="home-developer__cta-text">{t("homeDeveloperApiLead")}</p>
        <a
          className="btn btn-primary home-developer__cta-button"
          href={apiPath}
          onClick={() => trackEvent({ eventType: "DeveloperCtaClicked", pagePath: window.location.pathname })}
        >
          {t("homeDeveloperApiCta")} <span aria-hidden="true">→</span>
        </a>
      </div>
    </section>
  );
}

function DevCard({
  icon,
  title,
  description,
  command,
  note,
  resourceUrl,
  resourceLabel,
  copyLabel,
  copiedLabel,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  command: string;
  note?: string;
  resourceUrl: string;
  resourceLabel: string;
  copyLabel: string;
  copiedLabel: string;
  children?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  async function copyCommand() {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <article className="home-dev-card">
      <div className="home-dev-card__icon" aria-hidden="true">{icon}</div>
      <h3 className="home-dev-card__title">{title}</h3>
      <p className="home-dev-card__description">{description}</p>
      <div className="home-dev-card__command">
        <span className="home-dev-card__prompt" aria-hidden="true">$</span>
        <code>{command}</code>
        <button
          type="button"
          className="home-dev-card__copy"
          onClick={copyCommand}
          aria-label={`${copyLabel}: ${command}`}
        >
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
      {children}
      {note && <p className="home-dev-card__note">{note}</p>}
      <a className="home-dev-card__github" href={resourceUrl} target="_blank" rel="noopener noreferrer">
        {resourceLabel} <span aria-hidden="true">→</span>
      </a>
    </article>
  );
}

function TerminalIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m7 9 3 3-3 3" />
      <path d="M13 15h4" />
    </svg>
  );
}

function BracketsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 8-4 4 4 4" />
      <path d="m15 8 4 4-4 4" />
    </svg>
  );
}

function PackageIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
      <path d="m4.5 7.75 7.5 4.2 7.5-4.2" />
      <path d="M12 12v9" />
    </svg>
  );
}

function CubeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 9h16" />
      <path d="M9 9v11" />
    </svg>
  );
}
